import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, forkJoin, map, of, tap } from 'rxjs';
import {
  AdmissionFormResponse,
  AdmissionFormRequest,
  StudentPortalService,
} from '../../../core/services/student-portal.service';
import { ToastService } from '../../../core/services/toast.service';
import { ApplicantFieldsFormValue } from '../models/applicant-fields.model';
import { ApplicantFieldsActions } from './applicant-fields.actions';

export class ApplicantFieldsEffects {
  private readonly actions$ = inject(Actions);
  private readonly studentPortalService = inject(StudentPortalService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly loadPageData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicantFieldsActions.loadPageData),
      exhaustMap(() => {
        localStorage.removeItem('pise:applicant-fields');

        return forkJoin({
          profile: this.studentPortalService
            .getStudentProfileMe()
            .pipe(
              catchError(() =>
                this.studentPortalService.getProfile().pipe(catchError(() => of(null))),
              ),
            ),
          admissionForm: this.studentPortalService
            .getMyAdmissionForm()
            .pipe(catchError(() => of({} as AdmissionFormResponse))),
        }).pipe(
          map(({ profile, admissionForm }) =>
            ApplicantFieldsActions.loadPageDataSuccess({
              profile,
              savedFields: this.toApplicantFieldsFormValue(admissionForm),
            }),
          ),
          catchError(() => of(ApplicantFieldsActions.loadPageDataFailure({ savedFields: {} }))),
        );
      }),
    ),
  );

  readonly saveFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicantFieldsActions.saveFields),
      exhaustMap(({ value }) =>
        this.studentPortalService.saveMyAdmissionForm(this.toAdmissionFormRequest(value)).pipe(
          tap(() => {
            this.toastService.success(
              this.translateService.instant('applicantFields.messages.saved'),
            );
          }),
          map(() => ApplicantFieldsActions.saveFieldsSuccess({ value })),
          catchError((error) => {
            const message =
              typeof error?.error?.message === 'string'
                ? error.error.message
                : this.translateService.instant('applicantFields.messages.saveError');
            this.toastService.error(message);

            return of(ApplicantFieldsActions.saveFieldsFailure({ error: message }));
          }),
        ),
      ),
    ),
  );

  private toAdmissionFormRequest(value: ApplicantFieldsFormValue): AdmissionFormRequest {
    return {
      dateOfBirth: value.birthDate || null,
      gender: this.toGenderLabel(value.gender),
      nationality: value.nationality,
      curp: value.curp,
      rfc: value.rfc,
      maritalStatus: this.toMaritalStatusLabel(value.maritalStatus),
      birthState: value.birthPlace,
      birthCountry: '',
      street: value.address,
      neighborhood: '',
      municipality: '',
      city: '',
      state: '',
      postalCode: '',
      whatsApp: value.whatsappActive ? value.phone : '',
      alternatePhone: value.alternatePhone || null,
      preferredContactMethod: this.toContactMethodLabel(value.preferredContactMethod),
      acceptsEmailNotifications: value.acceptsEmailNotifications,
      acceptsWhatsAppNotifications: value.acceptsWhatsappNotifications,
      emergencyContactName: value.emergencyContact,
      emergencyContactRelation: '',
      emergencyContactPhone: '',
      previousInstitution: null,
      previousDegree: null,
      graduationYear: null,
      occupation: null,
      employer: null,
      notes: null,
      submit: false,
    };
  }

  private toApplicantFieldsFormValue(
    value: AdmissionFormResponse,
  ): Partial<ApplicantFieldsFormValue> {
    return {
      curp: value.curp || '',
      rfc: value.rfc || '',
      birthDate: value.dateOfBirth || '',
      birthPlace: value.birthState || '',
      nationality: value.nationality || '',
      gender: this.fromGenderLabel(value.gender || ''),
      maritalStatus: this.fromMaritalStatusLabel(value.maritalStatus || ''),
      address: value.street || '',
      alternatePhone: value.alternatePhone || '',
      emergencyContact: value.emergencyContactName || '',
      whatsappActive: Boolean(value.whatsApp),
      preferredContactMethod: this.fromContactMethodLabel(value.preferredContactMethod || ''),
      acceptsEmailNotifications: Boolean(value.acceptsEmailNotifications),
      acceptsWhatsappNotifications: Boolean(value.acceptsWhatsAppNotifications),
    };
  }

  private toGenderLabel(value: string): string {
    const labels: Record<string, string> = {
      female: 'Femenino',
      male: 'Masculino',
      nonBinary: 'No binario',
    };

    return labels[value] ?? value;
  }

  private toMaritalStatusLabel(value: string): string {
    const labels: Record<string, string> = {
      single: 'Soltero',
      married: 'Casado',
      domesticPartnership: 'Union libre',
      divorced: 'Divorciado',
      other: 'Otro',
    };

    return labels[value] ?? value;
  }

  private toContactMethodLabel(value: string): string {
    const labels: Record<string, string> = {
      whatsapp: 'WhatsApp',
      call: 'Llamada',
      email: 'Email',
    };

    return labels[value] ?? value;
  }

  private fromGenderLabel(value: string): string {
    return this.fromLabel(value, {
      femenino: 'female',
      female: 'female',
      masculino: 'male',
      male: 'male',
      'no binario': 'nonBinary',
      nonbinary: 'nonBinary',
    });
  }

  private fromMaritalStatusLabel(value: string): string {
    return this.fromLabel(value, {
      soltero: 'single',
      single: 'single',
      casado: 'married',
      married: 'married',
      'union libre': 'domesticPartnership',
      domesticpartnership: 'domesticPartnership',
      divorciado: 'divorced',
      divorced: 'divorced',
      otro: 'other',
      other: 'other',
    });
  }

  private fromContactMethodLabel(value: string): string {
    return this.fromLabel(value, {
      whatsapp: 'whatsapp',
      llamada: 'call',
      call: 'call',
      email: 'email',
      correo: 'email',
    });
  }

  private fromLabel(value: string, labels: Record<string, string>): string {
    const normalizedValue = value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

    return labels[normalizedValue] ?? value;
  }
}
